import React from 'react';
import {
  Flex,
  useColorMode,
  useIsMobileView,
  Text,
  color,
  typography,
} from 'deepstash-ui';
import { ArticleApiResponse, InsightApiResponse } from 'api/api.types';
import IdeaCard from 'src/components/idea-card/IdeaCard';
import { normalizeIdea } from 'utils/normalizers/source';
import LandingStartTrialButton from 'src/page-components/landing/components-v2/LandingStartTrialButton';

const IDEAS_COUNT = 3;

const IDEA_SIZE = { WIDTH: 340, HEIGHT: 480 };
const IDEA_SIZE_MOBILE = { WIDTH: 280, HEIGHT: 400 };
const IDEA_OVERLAY_HEIGHT = 200;
const IDEA_OVERLAY_HEIGHT_MOBILE = 160;
const IDEA_LAST_OVERLAY_HEIGHT = 470;
const IDEA_LAST_OVERLAY_HEIGHT_MOBILE = 390;

interface CollectionKeyIdeasSectionProps {
  articles: ArticleApiResponse[];
  totalIdeas: number;
}

const CollectionKeyIdeasSection: React.FC<CollectionKeyIdeasSectionProps> = ({
  articles,
  totalIdeas,
}) => {
  const isMobileView = useIsMobileView();
  const { colorMode } = useColorMode();

  const ideasShowed = React.useMemo(() => {
    const allFeaturedIdeas: InsightApiResponse[] = [];
    const allIdeas: InsightApiResponse[] = [];

    articles.forEach(article => {
      const featuredIdeasInArticle = article.blocks?.filter(
        block => block.is_featured_in_collection === true,
      );
      if (
        featuredIdeasInArticle !== undefined &&
        featuredIdeasInArticle.length > 0
      ) {
        allFeaturedIdeas.push(...featuredIdeasInArticle);
      }
      if (article.blocks !== undefined && article.blocks !== null) {
        allIdeas.push(...article.blocks);
      }
    });

    if (allFeaturedIdeas.length >= IDEAS_COUNT) {
      return allFeaturedIdeas.slice(0, IDEAS_COUNT);
    }

    // Return first 3 ideas from first source (sources)
    // and with content >= 400
    const filteredAllIdeas = allIdeas.filter(
      idea => idea.content.length >= 400,
    );

    return filteredAllIdeas.slice(0, IDEAS_COUNT);
  }, [articles]);

  return (
    <Flex flexDirection="column" width={'100%'}>
      <Text
        {...(isMobileView
          ? typography.primaryHeadingFonts.small.bold
          : typography.primaryHeadingFonts.large.bold)}
        color={color[colorMode].text}
        textAlign="center"
      >
        {`3 Top Key Ideas (${totalIdeas} total)`}
      </Text>
      <Flex
        flexDirection="row"
        gridColumnGap={16}
        mt={16}
        w={'100%'}
        overflow={{ base: 'scroll', md: 'hidden' }}
        justifyContent={isMobileView ? 'flex-start' : 'center'}
        {...(isMobileView ? { px: 16, pb: 16 } : {})}
      >
        {ideasShowed.map((idea, index) => {
          const normalizedIdea = normalizeIdea(idea);
          const isLastIdea = index === IDEAS_COUNT - 1;
          return (
            <Flex key={index} flexDirection="column" position="relative">
              <IdeaCard
                ideaData={{ idea: normalizedIdea, source: null }}
                isNotInteractable
                height={
                  isMobileView ? IDEA_SIZE_MOBILE.HEIGHT : IDEA_SIZE.HEIGHT
                }
                width={isMobileView ? IDEA_SIZE_MOBILE.WIDTH : IDEA_SIZE.WIDTH}
                hideImage={true}
                position="relative"
                disableTitleHref={true}
              />
              <Flex
                position="absolute"
                left={0}
                right={0}
                bottom={0}
                height={
                  isLastIdea
                    ? isMobileView
                      ? IDEA_LAST_OVERLAY_HEIGHT_MOBILE
                      : IDEA_LAST_OVERLAY_HEIGHT
                    : isMobileView
                    ? IDEA_OVERLAY_HEIGHT_MOBILE
                    : IDEA_OVERLAY_HEIGHT
                }
                background={
                  isLastIdea
                    ? 'linear-gradient(0deg, rgba(255,255,255,1) 56%, rgba(255,255,255,0.3))'
                    : 'linear-gradient(0deg, rgba(255,255,255,1) 10%, rgba(255,255,255,0.2))'
                }
                borderRadius={'1.25rem'} // Copy from IdeasCard
              >
                {isLastIdea ? (
                  <Flex
                    flexDirection="column"
                    justifyContent="flex-end"
                    alignItems="center"
                    pb={32}
                    px={32}
                    width={
                      isMobileView ? IDEA_SIZE_MOBILE.WIDTH : IDEA_SIZE.WIDTH
                    }
                  >
                    <Text
                      {...typography.primaryHeadingFonts.small.semiBold}
                      color={color[colorMode].text}
                      textAlign="center"
                    >
                      {`Want to learn more? Unlock this collection and all ${totalIdeas} key ideas.`}
                    </Text>
                    <LandingStartTrialButton
                      landingLocation={'collection-key-ideas'}
                      mt={24}
                    />
                  </Flex>
                ) : null}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};

export default CollectionKeyIdeasSection;
